<template>
  <div class="article-info__wrapper">
    <div
      v-if="isSponsored"
      class="article-info__category--sponsored text-medium bold"
    >
      Sponsored
    </div>
    <a-category-label-link
      class="article-info__category"
      :category="category"
      :hide-category="hideCategory"
    />
    <div class="article-info__publisher-wrapper">
      <div v-if="!hideCategory" class="article-info__dot" />
      <div class="article-info__publisher text-medium">
        <a-link
          v-if="publisherLinkProps"
          v-bind="publisherLinkProps"
          class="article-info__publisher-name bold"
        >
          {{ publisherName }}
        </a-link>
        <span v-else class="article-info__publisher-name bold">
          {{ publisherName }}
        </span>
      </div>
    </div>

    <div class="article-info__date-wrapper">
      <div class="article-info__dot" />
      <a-article-info-date
        :published-on="publishedOn"
        class="article-info__date"
      />
    </div>
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { pathOr } from 'ramda'
import { ROUTE_NAME } from 'enums/routes'
import AArticleInfoDate from 'shared/AArticleInfoDate'
import mixins from '@/utils/mixins'
import ACategoryLabelLink from 'shared/ACategoryLabelLink'

export default {
  name: 'AArticleInfo',
  mixins: [mixins.urlGenerators],
  components: { AArticleInfoDate, ACategoryLabelLink },
  props: {
    category: propValidator([PROP_TYPES.OBJECT], false),
    author: propValidator([PROP_TYPES.OBJECT], false),
    publishedOn: propValidator([PROP_TYPES.STRING]),
    sponsorshipCompany: propValidator([PROP_TYPES.OBJECT], false),
    hideCategory: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  computed: {
    isSponsored() {
      return !!this.sponsorshipCompany
    },
    publisherLinkProps() {
      if (!this.sponsorshipCompany) {
        return {
          to: this.authorLink
        }
      }

      if (this.linkedCompany) {
        const { slug, categorySlug } = this.linkedCompany
        if (!slug || !categorySlug) return null

        return {
          to: this.companyUrl,
          trailingSlash: this.$helper.isLinkWithTrailingSlash(this.companyUrl)
        }
      } else {
        return null
      }
    },
    companyUrl() {
      if (!this.linkedCompany) return null

      return this.generateCompanyPath(this.linkedCompany)
    },
    authorLink() {
      if (!this.author || !this.author.slug) return null

      return {
        name: ROUTE_NAME.AUTHOR_AUTHOR,
        params: {
          author: this.author.slug
        }
      }
    },
    linkedCompany() {
      return this.sponsorshipCompany && this.sponsorshipCompany.linkedCompany
    },
    publisherName() {
      const authorName = this.author.name
      const companyName = pathOr(null, ['name'], this.sponsorshipCompany)

      return companyName || authorName
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style lang="scss" scoped>
$item-margin-top: 6px;

.article-info__wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  // negative margin to make sure the elements have some gap
  // during wrapping
  margin-top: -#{$item-margin-top};

  .article-info__category--sponsored {
    padding: 4px 10px;
    background-color: $c--gray-soft;
    border-radius: 6px;
    margin-top: $item-margin-top;
    margin-right: 8px;
    color: $c--gray-2;
    font-weight: 400;

    &:hover {
      color: $c--gray-2;
    }
  }

  .article-info__category {
    margin: 6px 8px 0 0;
  }

  .article-info__publisher-wrapper {
    margin-top: $item-margin-top;
    margin-right: 8px;
    display: flex;
    align-items: center;
  }

  .article-info__publisher {
    color: $c--gray-dark-2;
  }

  .article-info__publisher-name {
    color: $c--mid-purple;

    &:hover {
      color: $c--main;
      text-decoration: underline;
    }
  }

  .article-info__date-wrapper {
    margin-top: $item-margin-top;
    display: flex;
    align-items: center;
  }

  .article-info__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: $c--gray-dark;
    opacity: 0.3;
  }
}
</style>
